/**
 * ドメインビジュアル定数 — Single Source of Truth
 *
 * 6つの SRE ドメインに対応する色・ラベルを Record 型で網羅的に定義。
 * ui-design: HSL 10段階パレットの 500（primary）/ 50（bg）/ 100（light）を使用
 * ux-psychology: Von Restorff 効果 — 各ドメインに固有の色相で即座の分類を可能に
 * effective-typescript: Record<SreDomain, T> で新ドメイン追加時のコンパイルエラーを保証
 */

import type {
  SreDomain,
  AgentStatusKind,
  DomainVisual,
  ActionType,
  ImpactLevel,
} from '../types/agentTypes';

// ── ドメインビジュアル ────────────────────────────────
// Record<SreDomain, DomainVisual> — 全6ドメインの網羅が必須
export const DOMAIN_VISUALS: Record<SreDomain, DomainVisual> = {
  security: {
    label: 'セキュリティ',
    color: '#E12D39',     // Red Vivid 400
    bgColor: '#FFE3E3',   // Red Vivid 900
    lightColor: '#FFBDBD', // Red Vivid 800
  },
  cost: {
    label: 'コスト',
    color: '#57AE5B',     // Green 500
    bgColor: '#E3F9E5',   // Green 900
    lightColor: '#C1EAC5', // Green 800
  },
  performance: {
    label: 'パフォーマンス',
    color: '#724BB7',     // Purple 400
    bgColor: '#EAE2F8',   // Purple 900
    lightColor: '#D6BCFA', // Purple 800
  },
  iam: {
    label: 'IAM監査',
    color: '#F0B429',     // Yellow Vivid 400
    bgColor: '#FFFBEA',   // Yellow Vivid 900
    lightColor: '#FFF3C4', // Yellow Vivid 800
  },
  log: {
    label: 'ログ分析',
    color: '#38BEC9',     // Cyan 500
    bgColor: '#E0FCFF',   // Cyan 900
    lightColor: '#BEF8FD', // Cyan 800
  },
  integrated: {
    label: '統合レポート',
    color: '#2680C2',     // Blue 400
    bgColor: '#DCEEFB',   // Blue 900
    lightColor: '#B6E0FE', // Blue 800
  },
};

// ── エージェントステータスカラー ──────────────────────
// Record<AgentStatusKind, string> — 全4状態の網羅が必須
export const STATUS_COLORS: Record<AgentStatusKind, string> = {
  idle: '#94a3b8',
  running: '#3b82f6',
  completed: '#22c55e',
  error: '#ef4444',
};

export const STATUS_BG_COLORS: Record<AgentStatusKind, string> = {
  idle: '#f1f5f9',
  running: '#eff6ff',
  completed: '#f0fdf4',
  error: '#fef2f2',
};

export const STATUS_LABELS: Record<AgentStatusKind, string> = {
  idle: '待機中',
  running: '実行中',
  completed: '完了',
  error: 'エラー',
};

// ── アクションタイプ ──────────────────────────────────
export const ACTION_TYPE_LABELS: Record<ActionType, string> = {
  terraform: 'TF',
  command: 'CMD',
  runbook: 'RB',
  manual: '手動',
};

export const ACTION_TYPE_COLORS: Record<ActionType, string> = {
  terraform: '#7c3aed',
  command: '#0369a1',
  runbook: '#047857',
  manual: '#64748b',
};

// ── 影響度カラー ──────────────────────────────────────
export const IMPACT_COLORS: Record<
  ImpactLevel,
  { readonly bg: string; readonly text: string }
> = {
  high: { bg: '#fee2e2', text: '#dc2626' },
  medium: { bg: '#fef3c7', text: '#d97706' },
  low: { bg: '#d1fae5', text: '#059669' },
};
